import { Settings } from 'luxon';
import browserLanguage from 'Utility/browserLanguage';
import { TDispatch } from '..';
import {
  fetchOrganizationSettings,
  fetchUserSettings,
  fetchUserSettingsVault,
  postUserSettingsVault,
} from './apiFetchers';
import { createAction, createReducer } from '@reduxjs/toolkit';
import { isAttachmentPath } from 'Utility/location';

const LOAD_ORGSETTINGS = 'neuro-ui/settings/LOAD_ORGSETTINGS';
const LOAD_USERSETTINGS = 'neuro-ui/settings/LOAD_USERSETTINGS';
const LOAD_USERSETTINGS_FROM_VAULT = 'neuro-ui/settings/LOAD_USERSETTINGS_FROM_VAULT';
const UPDATE_USERSETTINGS_VAULT = 'neuro-ui/settings/UPDATE_USERSETTINGS_VAULT';

const loadOrgSettingsAction = createAction(LOAD_ORGSETTINGS, (orgSettings: IOrgSettings) => {
  return {
    payload: { orgSettings },
  };
});

const loadUserSettingsAction = createAction(LOAD_USERSETTINGS, (userSettings: IUserSettings) => {
  return {
    payload: { userSettings },
  };
});

const loadUserSettingsFromVaultAction = createAction(LOAD_USERSETTINGS_FROM_VAULT, (userSettings: IUserSettings) => {
  return {
    payload: { userSettings },
  };
});

const updateUserSettingsVaultAction = createAction(UPDATE_USERSETTINGS_VAULT, (userSettings: IUserSettings) => {
  return {
    payload: { userSettings },
  };
});

const loadOrgSettings =
  () =>
  (dispatch: TDispatch): void => {
    fetchOrganizationSettings()
      .then(data => {
        if (data[0]) {
          dispatch(loadOrgSettingsAction(data[0].settings));
        }
        if (data[1]) {
          dispatch(loadOrgSettingsAction({ messagingSettings: data[1].settings }));
        }
      })
      .catch(() => {
        return;
      });
  };

const loadUserSettings =
  () =>
  (dispatch: TDispatch): void => {
    fetchUserSettings()
      .then(data => {
        if (data?.settings) {
          const settings = data.settings;
          const settingNames = Object.keys(settings);

          const entries = settingNames.map(name => [name, settings[name]['value']]);
          const newData = Object.fromEntries(entries);
          dispatch(loadUserSettingsAction(newData));
        }
      })
      .catch(() => {
        return;
      });
    if (isAttachmentPath()) {
      dispatch(loadUserSettingsFromVaultAction({ uiLanguage: browserLanguage }));
      return;
    }
    fetchUserSettingsVault()
      .then(data => data && dispatch(loadUserSettingsFromVaultAction(data)))
      .catch(() => {
        return;
      });
  };

const createUserSettingVault =
  (data: IUserSettings) =>
  async (dispatch: TDispatch): Promise<void> => {
    return await postUserSettingsVault(data).then(res => {
      const { uiLanguage, ...rest } = data;
      if (res && res[0]) dispatch(updateUserSettingsVaultAction({ uiLanguage }));
      if (res && res[1]) dispatch(updateUserSettingsVaultAction(rest));
    });
  };

export const actions = {
  loadOrgSettings,
  loadUserSettings,
  createUserSettingVault,
};

const initialState: ISettingsStore = { orgSettings: {}, userSettings: {} };

const reducer = createReducer(initialState, builder => {
  builder
    .addCase(loadOrgSettingsAction, (state, action) => {
      state.orgSettings = { ...state.orgSettings, ...action.payload.orgSettings };
    })
    .addCase(loadUserSettingsAction, (state, action) => {
      state.userSettings = { ...state.userSettings, ...action.payload.userSettings };
    })
    .addCase(loadUserSettingsFromVaultAction, (state, action) => {
      const uiLanguage = action.payload.userSettings.uiLanguage || browserLanguage;
      // Set locale for luxon
      Settings.defaultLocale = uiLanguage;
      state.userSettings = { ...state.userSettings, ...action.payload.userSettings };
    })
    .addCase(updateUserSettingsVaultAction, (state, action) => {
      // Update luxon setting as well
      const uiLanguage = action.payload.userSettings.uiLanguage;
      if (uiLanguage) Settings.defaultLocale = uiLanguage;

      state.userSettings = { ...state.userSettings, ...action.payload.userSettings };
    })
    .addDefaultCase(state => {
      return state;
    });
});

export default reducer;
